/**
 * StatusPill: the small uppercase state pill of the Settings screens (DESIGN.md
 * sections 7 and 13.1): ring geometry, never a border, and the state in words
 * inside the pill, so colour is never the only signal.
 *
 * Five tones, each one meaning (DESIGN.md section 1):
 *   - `on`: emerald. Connected, active, running.
 *   - `off`: muted grey. Off, revoked, history. Quiet, not a warning.
 *   - `warning`: amber. Needs attention, or a setting that does nothing.
 *   - `error`: red. A terminal or failed state.
 *   - `transit`: cyan. On its way and on time (an Order and wait box, BLU-1506);
 *     the PO status palette's "In Transit" (`STATUS_SELECT_CLASS.in_transit`),
 *     so a delivery in flight reads the same on every purchase order surface.
 *
 * The words are the caller's, already translated in its own namespace; this
 * component never calls `useTranslation`. `srLabel` names what the state is
 * about for screen readers ("Store SKUs for standard products: On") when the
 * pill sits beside a heading a screen reader would not pair it with.
 *
 * `role` passes through and has no default: give `role="status"` only to a
 * pill whose words change while the page is open, so the change is announced.
 *
 * Not built on shadcn `Badge`: section 13.1 draws the pill with `ring-1`, and
 * Badge's own border breaks that silhouette. Never hand-roll another one.
 */
import type { HTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type StatusPillTone = 'on' | 'off' | 'warning' | 'error' | 'transit';

const PILL_BASE =
  'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1';

const PILL_TONE: Record<StatusPillTone, string> = {
  on: 'bg-emerald-100 text-emerald-800 ring-emerald-300/80',
  off: 'bg-muted text-muted-foreground ring-border',
  warning: 'bg-amber-100 text-amber-800 ring-amber-300/80',
  error: 'bg-red-100 text-red-800 ring-red-300/80',
  transit: 'bg-cyan-100 text-cyan-800 ring-cyan-300/80',
};

function statusPillClass(tone: StatusPillTone): string {
  return cn(PILL_BASE, PILL_TONE[tone]);
}

export interface StatusPillProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  tone: StatusPillTone;
  /** The state in words, already translated. */
  children: ReactNode;
  /** An optional glyph before the words; decorative, the words carry the meaning. */
  icon?: LucideIcon;
  /** Read first by screen readers only, followed by ": " ("Automatic set-up: On"). */
  srLabel?: string;
}

export function StatusPill({ tone, children, icon: Icon, srLabel, className, ...rest }: StatusPillProps) {
  return (
    <span {...rest} className={cn(statusPillClass(tone), className)}>
      {srLabel && <span className="sr-only">{srLabel}: </span>}
      {Icon && <Icon className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />}
      {children}
    </span>
  );
}
